import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    typeProjet: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setIsSubmitting(true);
    setSubmitStatus('');
    try {
      const submitData = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => submitData.append(k, v));
      const response = await fetch('https://readdy.ai/api/form/d2rbmvijqms3b5jftro0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: submitData.toString()
      });
      setSubmitStatus(response.ok ? 'success' : 'error');
      if (response.ok) setFormData({ nom: '', email: '', telephone: '', typeProjet: '', message: '' });
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section id="contact" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">

        <div className="text-center mb-14">
          <span className="inline-block bg-orange-100 text-orange-600 text-sm font-bold px-4 py-1.5 rounded-full mb-4">Contact</span>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-slate-900 mb-4">
            Obtenez votre devis<br />
            <span className="text-orange-500">100% gratuit et sans engagement</span>
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            Remplissez le formulaire et un expert vous rappelle sous 2h. Pas de démarchage abusif, promis.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10">
          {/* Form */}
          <div className="lg:col-span-3 bg-white rounded-3xl p-8 lg:p-10">
            <h3 className="text-2xl font-extrabold text-slate-900 mb-7">Votre demande de devis</h3>

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="nom" className="block text-sm font-semibold text-slate-700 mb-1.5">Nom complet *</label>
                  <input
                    type="text" id="nom" name="nom" required
                    value={formData.nom} onChange={handleChange}
                    placeholder="Votre nom complet"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none transition-all"
                  />
                </div>
                <div>
                  <label htmlFor="telephone" className="block text-sm font-semibold text-slate-700 mb-1.5">Téléphone *</label>
                  <input
                    type="tel" id="telephone" name="telephone" required
                    value={formData.telephone} onChange={handleChange}
                    placeholder="01 89 29 33 23"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none transition-all"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-1.5">Email *</label>
                <input
                  type="email" id="email" name="email" required
                  value={formData.email} onChange={handleChange}
                  placeholder="votre.email@exemple.com"
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none transition-all"
                />
              </div>

              <div>
                <label htmlFor="typeProjet" className="block text-sm font-semibold text-slate-700 mb-1.5">Type de projet *</label>
                <select
                  id="typeProjet" name="typeProjet" required
                  value={formData.typeProjet} onChange={handleChange}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none transition-all"
                >
                  <option value="">Choisissez votre projet</option>
                  <option value="pompe-chaleur-air-air">Pompe à chaleur air/air</option>
                  <option value="pompe-chaleur-air-eau">Pompe à chaleur air/eau</option>
                  <option value="panneaux-photovoltaiques">Panneaux photovoltaïques</option>
                  <option value="solaire-combine">Système solaire combiné</option>
                  <option value="eclairage-led">Éclairage LED</option>
                  <option value="deshumidificateur">Déshumidificateur</option>
                  <option value="audit-energetique">Audit énergétique</option>
                  <option value="autre">Autre projet</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-1.5">Décrivez votre projet</label>
                <textarea
                  id="message" name="message" rows={4}
                  value={formData.message} onChange={handleChange}
                  maxLength={500}
                  placeholder="Surface du logement, situation actuelle (chaudière gaz/fioul), vos questions..."
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-400 focus:border-transparent outline-none transition-all resize-none"
                ></textarea>
                <p className="text-xs text-slate-400 mt-1">{formData.message.length}/500 caractères</p>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
                  <i className="ri-checkbox-circle-line text-emerald-500 text-xl flex-shrink-0"></i>
                  <p className="text-emerald-800 text-sm font-medium">Demande envoyée ! Nous vous recontactons dans les 2h.</p>
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                  <i className="ri-error-warning-line text-red-500 text-xl flex-shrink-0"></i>
                  <p className="text-red-800 text-sm font-medium">Une erreur s'est produite. Appelez-nous directement au 01 89 29 33 23.</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || formData.message.length > 500}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-slate-300 text-white font-extrabold py-4 px-6 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-base"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-lg"></i>Envoi en cours...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-send-plane-fill text-lg"></i>Recevoir mon devis gratuit
                  </span>
                )}
              </button>
            </form>
          </div>

          {/* Right column */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            {/* Urgency CTA */}
            <div className="bg-slate-900 rounded-3xl p-8 text-white">
              <p className="text-sm font-semibold text-slate-400 mb-2">Appel gratuit</p>
              <a href="tel:0189293323" className="text-3xl font-extrabold text-white hover:text-orange-400 transition-colors cursor-pointer block mb-1">01 89 29 33 23</a>
              <p className="text-slate-400 text-sm mb-6">Lun–Ven · 9h à 18h</p>
              <a href="tel:0189293323" className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold py-3.5 px-6 rounded-xl transition-colors cursor-pointer whitespace-nowrap w-full">
                <i className="ri-phone-fill text-lg"></i>Nous appeler maintenant
              </a>
            </div>

            {/* Coordinates */}
            <div className="bg-white rounded-3xl p-8 flex-1">
              <h3 className="font-extrabold text-slate-900 text-lg mb-6">Nos coordonnées</h3>
              <div className="space-y-5">
                {[
                  { icon: 'ri-map-pin-2-line', color: 'bg-orange-100 text-orange-500', label: 'Adresse', val: '14 rue du Cadran\n94220 Charenton-le-Pont' },
                  { icon: 'ri-mail-line', color: 'bg-teal-100 text-teal-500', label: 'Email', val: 'contact@air-pure-groupe.fr' },
                  { icon: 'ri-time-line', color: 'bg-emerald-100 text-emerald-500', label: 'Réponse garantie', val: 'Sous 2h en semaine' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className={`w-10 h-10 ${item.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400 font-medium mb-0.5">{item.label}</p>
                      <p className="text-slate-800 font-semibold text-sm whitespace-pre-line">{item.val}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Commitments */}
            <div className="bg-emerald-500 rounded-3xl p-7 text-white">
              <h4 className="font-extrabold text-lg mb-4">Nos engagements</h4>
              <ul className="space-y-3">
                {[
                  'Devis 100% gratuit & sans engagement',
                  'Réponse sous 2h en semaine',
                  'Accompagnement pour les aides de l\'État',
                  'Installation par des techniciens RGE',
                ].map((c, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm font-medium text-emerald-50">
                    <i className="ri-checkbox-circle-fill text-white text-base flex-shrink-0"></i>
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
